// frontend/src/pages/PricingPage.tsx
//
// Pricing page showing subscription tiers and PAYG credit packages.

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { usePricing, formatPrice, PlanInfo, PaygPackage } from '../hooks/usePricing';
import { Check, X, ArrowRight, Loader2, Crown, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Coins, ShoppingCart } from 'lucide-react';
import { SPONSORED_AI_CALL_COST, BUILDER_AI_PER_30_SEC_COST } from '../lib/supabase';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';

// Helper to clean up plan names (remove "Llambada - " prefix)
function formatPlanName(name: string): string {
  return name.replace(/^Llambada\s*-\s*/i, '');
}

type BillingPeriod = 'payg' | 'monthly' | 'yearly';

// Key features shown in plan cards
interface KeyFeatures {
  credits: string;
  complimentaryCoderCalls: string;
  miniAppAiCalls: string;
  hasAds: boolean;
}

// All features for comparison table
interface AllFeatures {
  credits: string;
  complimentaryCoderCalls: string;
  miniAppAiCalls: string;
  customMiniApps: string;
  publishing: string;
  maxProcessingTime: string;
  maxPayloadSize: string;
  fileStorage: string;
  appData: string;
  vectors: string;
  backendReqs: string;
  embeddingBatch: string;
  hasAds: boolean;
}

// Compute credit-dependent key features from subscription credits
function getKeyFeatures(tier: string, subscriptionCredits: number | null): KeyFeatures {
  // Free tier defaults
  const credits = subscriptionCredits || (tier === 'free' ? 100 : 500);
  const coderCalls = Math.floor(credits / SPONSORED_AI_CALL_COST);
  const hasAds = tier === 'free' || tier === 'standard_with_ads';

  return {
    credits: `${credits.toLocaleString()} credits/month`,
    complimentaryCoderCalls: `~${coderCalls} complimentary AI coder calls`,
    miniAppAiCalls: `~${coderCalls} AI calls in mini-apps`,
    hasAds,
  };
}

// Static (non-credit) features by tier
const TIER_STATIC_FEATURES: Record<string, Omit<AllFeatures, 'credits' | 'complimentaryCoderCalls' | 'miniAppAiCalls' | 'hasAds'>> = {
  free: {
    customMiniApps: '1 custom mini-app',
    publishing: 'Publish 1 mini-app',
    maxProcessingTime: '10 minutes',
    maxPayloadSize: '10 MB',
    fileStorage: '250 MB file storage (total)',
    appData: '1 MB app data/app',
    vectors: '1,000 semantic memory vectors',
    backendReqs: '100 req/hr backend',
    embeddingBatch: '10 texts/embedding batch',
  },
  standard_with_ads: {
    customMiniApps: 'Unlimited custom mini-apps',
    publishing: 'Publish unlimited mini-apps',
    maxProcessingTime: '1 hour',
    maxPayloadSize: '20 MB',
    fileStorage: '1 GB file uploads/month',
    appData: '5 MB app data/app',
    vectors: '5,000 semantic memory vectors',
    backendReqs: '100 req/hr backend',
    embeddingBatch: '50 texts/embedding batch',
  },
  standard: {
    customMiniApps: 'Unlimited custom mini-apps',
    publishing: 'Publish unlimited mini-apps',
    maxProcessingTime: '1 hour',
    maxPayloadSize: '20 MB',
    fileStorage: '1 GB file uploads/month',
    appData: '25 MB app data/app',
    vectors: '25,000 semantic memory vectors',
    backendReqs: '300 req/hr backend',
    embeddingBatch: '100 texts/embedding batch',
  },
  premium: {
    customMiniApps: 'Unlimited custom mini-apps',
    publishing: 'Publish unlimited mini-apps',
    maxProcessingTime: '8 hours',
    maxPayloadSize: '64 MB',
    fileStorage: '10 GB file uploads/month',
    appData: '100 MB app data/app',
    vectors: '100,000 semantic memory vectors',
    backendReqs: '300 req/hr backend',
    embeddingBatch: '200 texts/embedding batch',
  },
};

// Compute all features (credit-dependent + static) for a tier
function getAllFeatures(tier: string, subscriptionCredits: number | null): AllFeatures {
  const keyFeatures = getKeyFeatures(tier, subscriptionCredits);
  const staticFeatures = TIER_STATIC_FEATURES[tier] || TIER_STATIC_FEATURES.free;
  return { ...keyFeatures, ...staticFeatures };
}

// Free tier info (not in Stripe)
const FREE_TIER: PlanInfo = {
  tier: 'free',
  name: 'Free',
  description: 'For trying out Llambada',
  features: [],
  subscriptionCredits: 100,
  prices: {},
};

export function PricingPage() {
  const { user, subscriptionTier, isPaidUser, subscriptionExpiresAt } = useAuth();
  const { loading: checkoutLoading, error: checkoutError, startCheckout, openCustomerPortal, cancelSubscription, clearError } = useSubscription();
  const {
    plans: stripePlans,
    paygPackages,
    paymentsDisabled: pricingPaymentsDisabled,
    message: pricingMessage,
    loading: pricingLoading,
    error: pricingError,
    refetch,
  } = usePricing();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [showComparison, setShowComparison] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelResult, setCancelResult] = useState<{ success: boolean; message: string } | null>(null);
  const comparisonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Pricing - Llambada';
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const original = meta?.content || '';
    if (meta) meta.content = 'Llambada pricing plans — start free with 100 credits/month, or upgrade for unlimited mini-apps, longer processing, and more storage. Pay-as-you-go credits available.';
    return () => {
      document.title = 'Llambada';
      if (meta) meta.content = original;
    };
  }, []);

  // Combine free tier with Stripe plans
  const paymentsDisabled = PAYMENTS_DISABLED || pricingPaymentsDisabled;
  const paymentsDisabledMessage = pricingMessage || PAYMENTS_DISABLED_MESSAGE;
  const availablePaygPackages = paymentsDisabled ? [] : paygPackages;
  const allPlans = [FREE_TIER, ...stripePlans];

  const handleToggleComparison = () => {
    const willShow = !showComparison;
    setShowComparison(willShow);
    if (willShow) {
      setTimeout(() => {
        comparisonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleUpgrade = async (plan: PlanInfo) => {
    if (paymentsDisabled) return;

    const price = billingPeriod === 'yearly' ? plan.prices.yearly : plan.prices.monthly;
    if (!price) return;
    
    await startCheckout({ priceId: price.id });
  };

  const handleBuyPayg = async (pkg: PaygPackage) => {
    if (paymentsDisabled) return;

    await startCheckout({ priceId: pkg.price.id, paygCredits: pkg.credits });
  };

  const handleCancelSubscription = async () => {
    const result = await cancelSubscription(false);
    if (result) {
      setCancelResult({ success: true, message: result.message });
      setShowCancelModal(false);
    }
  };

  const error = checkoutError || pricingError;
  const loading = checkoutLoading;

  if (pricingLoading && stripePlans.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-theme-primary" />
        </div>
      </Layout>
    );
  }

  // Comparison table row labels
  const comparisonRows = [
    { key: 'credits', label: 'Monthly credits' },
    { key: 'complimentaryCoderCalls', label: 'Complimentary AI coder calls' },
    { key: 'miniAppAiCalls', label: 'AI calls in mini-apps' },
    { key: 'customMiniApps', label: 'Custom mini-apps' },
    { key: 'publishing', label: 'Publishing' },
    { key: 'maxProcessingTime', label: 'Max processing time per task' },
    { key: 'maxPayloadSize', label: 'Max file/payload size' },
    { key: 'fileStorage', label: 'File storage' },
    { key: 'appData', label: 'App data per app' },
    { key: 'vectors', label: 'Semantic memory vectors' },
    { key: 'backendReqs', label: 'Backend requests/hour' },
    { key: 'embeddingBatch', label: 'Embedding batch size' },
    { key: 'hasAds', label: 'Ad-free experience' },
  ];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="font-display text-2xl sm:text-4xl font-bold text-theme-text mb-3 sm:mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-theme-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            Start free and upgrade when you need more power. All plans include access to our full library of mini-apps.
          </p>
        </div>

        {/* Credit cost explainer */}
        <div className="max-w-2xl mx-auto mb-8 p-4 bg-theme-surface rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="h-5 w-5 text-theme-primary" />
            <span className="font-medium text-theme-text">How credits work</span>
          </div>
          <p className="text-sm text-theme-text-secondary">
            Credits are Llambada's currency for AI and data processing. Complimentary AI coder calls and AI calls in mini-apps cost <strong>{SPONSORED_AI_CALL_COST} credits</strong> each. Every 30 seconds of processing time costs <strong>{BUILDER_AI_PER_30_SEC_COST} credits</strong>.
          </p>
        </div>

        {paymentsDisabled && (
          <div className="max-w-2xl mx-auto mb-8 p-4 bg-theme-surface border border-theme-border-muted rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-theme-text">Payments are disabled</p>
                <p className="text-sm text-theme-text-secondary mt-1">
                  {paymentsDisabledMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Billing toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex flex-wrap items-center justify-center gap-1 sm:gap-2 bg-theme-surface rounded-lg p-1">
            {availablePaygPackages.length > 0 && (
              <button
                onClick={() => setBillingPeriod('payg')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingPeriod === 'payg'
                    ? 'bg-theme-primary text-white'
                    : 'text-theme-text-secondary hover:text-theme-text'
                }`}
              >
                Pay as you go
              </button>
            )}
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'monthly'
                  ? 'bg-theme-primary text-white'
                  : 'text-theme-text-secondary hover:text-theme-text'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'yearly'
                  ? 'bg-theme-primary text-white'
                  : 'text-theme-text-secondary hover:text-theme-text'
              }`}
            >
              Yearly
              <span className="ml-1 text-xs opacity-75">(Save more)</span>
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-theme-error-bg border border-theme-error rounded-lg">
            <p className="text-theme-error text-sm">{error}</p>
            <button 
              onClick={() => { clearError(); refetch(); }}
              className="text-theme-error text-sm underline mt-2 flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" /> Retry
            </button>
          </div>
        )}

        {/* Subscription pricing cards */}
        {billingPeriod !== 'payg' && <div className={`grid gap-8 mb-12 ${
          allPlans.length === 4 ? 'md:grid-cols-4' : 
          allPlans.length === 3 ? 'md:grid-cols-3' : 
          'md:grid-cols-2'
        }`}>
          {allPlans.map((plan) => {
            const price = billingPeriod === 'yearly' ? plan.prices.yearly : plan.prices.monthly;
            const isCurrentPlan = subscriptionTier === plan.tier;
            const isFree = plan.tier === 'free';
            const keyFeatures = getKeyFeatures(plan.tier, plan.subscriptionCredits);

            return (
              <div
                key={plan.tier}
                className={`card p-6 relative ${
                  plan.highlighted ? 'ring-2 ring-theme-primary' : ''
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-theme-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {isCurrentPlan && !isFree && !keyFeatures.hasAds && (
                  <div className="absolute top-4 right-4">
                    <Crown className="h-5 w-5 text-theme-primary" />
                  </div>
                )}

                <div className="mb-6">
                  <h2 className="font-display text-xl font-semibold text-theme-text mb-2">
                    {formatPlanName(plan.name)}
                  </h2>
                  <p className="text-theme-text-secondary text-sm">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-6">
                  {isFree ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-theme-text">$0</span>
                      <span className="text-theme-text-secondary">/forever</span>
                    </div>
                  ) : price ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-theme-text">
                          {formatPrice(price.amount, price.currency)}
                        </span>
                        <span className="text-theme-text-secondary">
                          /{price.interval === 'year' ? 'year' : 'month'}
                        </span>
                      </div>
                      {price.savings && price.savings > 0 && (
                        <span className="text-theme-success text-sm font-medium">
                          Save {formatPrice(price.savings, price.currency)}
                        </span>
                      )}
                    </>
                  ) : (
                    <div className="text-theme-text-secondary">
                      {billingPeriod === 'yearly' ? 'No yearly plan' : 'No monthly plan'}
                    </div>
                  )}
                </div>

                {/* Key features - credit focused */}
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-2">
                    <Coins className="h-5 w-5 text-theme-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-theme-text">
                      {keyFeatures.credits}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-theme-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-theme-text-secondary">
                      {keyFeatures.complimentaryCoderCalls}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-theme-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-theme-text-secondary">
                      {keyFeatures.miniAppAiCalls}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    {keyFeatures.hasAds ? (
                      <>
                        <X className="h-5 w-5 text-theme-text-muted flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-theme-text-muted">Contains ads</span>
                      </>
                    ) : (
                      <>
                        <Check className="h-5 w-5 text-theme-success flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-theme-text-secondary">No ads</span>
                      </>
                    )}
                  </li>
                </ul>

                {/* Compare plans link */}
                <button
                  onClick={handleToggleComparison}
                  className="text-sm text-theme-primary hover:underline mb-6 flex items-center gap-1"
                >
                  Compare plans
                  {showComparison ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {/* CTA Button */}
                {paymentsDisabled && !isFree ? (
                  <button disabled className="btn btn-secondary btn-block opacity-50">
                    Payments Disabled
                  </button>
                ) : isCurrentPlan ? (
                  <button
                    onClick={() => openCustomerPortal()}
                    disabled={loading || isFree}
                    className="btn btn-secondary btn-block"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    ) : isFree ? (
                      'Current Plan'
                    ) : (
                      'Manage Subscription'
                    )}
                  </button>
                ) : isFree ? (
                  isPaidUser ? (
                    <button
                      onClick={() => openCustomerPortal()}
                      disabled={loading}
                      className="btn btn-secondary btn-block"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      ) : (
                        'Manage Subscription'
                      )}
                    </button>
                  ) : (
                    <div className="btn btn-secondary btn-block opacity-50 cursor-default">
                      Current Plan
                    </div>
                  )
                ) : !user ? (
                  <Link to="/login" className="btn btn-primary btn-block">
                    Sign in to Upgrade
                  </Link>
                ) : !price ? (
                  <button disabled className="btn btn-secondary btn-block opacity-50">
                    Not Available
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan)}
                    disabled={loading}
                    className={`btn btn-block ${plan.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    ) : (
                      <>
                        Upgrade to {formatPlanName(plan.name)}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>}

        {/* PAYG Credit Packages */}
        {billingPeriod === 'payg' && availablePaygPackages.length > 0 && (
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="font-display text-2xl font-bold text-theme-text mb-2">
                <ShoppingCart className="h-6 w-6 inline-block mr-2 -mt-1" />
                Pay As You Go
              </h2>
              <p className="text-theme-text-secondary">
                Buy credit packs anytime. Credits roll over monthly and expire after three years.
              </p>
              {user && isPaidUser && (
                <p className="text-sm text-theme-text-muted mt-1">
                  Your subscription credits are used first. PAYG credits kick in as backup.
                </p>
              )}
            </div>

            <div className={`grid gap-6 max-w-3xl mx-auto ${
              availablePaygPackages.length === 3 ? 'md:grid-cols-3' :
              availablePaygPackages.length === 2 ? 'md:grid-cols-2' :
              'md:grid-cols-1'
            }`}>
              {availablePaygPackages.map((pkg) => (
                <div key={pkg.price.id} className="card p-6 text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Coins className="h-6 w-6 text-theme-primary" />
                    <span className="text-2xl font-bold text-theme-text">
                      {pkg.credits.toLocaleString()}
                    </span>
                    <span className="text-theme-text-secondary">credits</span>
                  </div>

                  <div className="text-3xl font-bold text-theme-text mb-1">
                    {formatPrice(pkg.price.amount, pkg.price.currency)}
                  </div>

                  <p className="text-xs text-theme-text-muted mb-4">
                    {formatPrice(Math.round(pkg.pricePerCredit * 100) / 100, pkg.price.currency)}/credit
                  </p>

                  {!user ? (
                    <Link to="/login" className="btn btn-secondary btn-block">
                      Sign in to Buy
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleBuyPayg(pkg)}
                      disabled={loading}
                      className="btn btn-secondary btn-block"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      ) : (
                        'Buy Now'
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comparison Table */}
        {showComparison && (
          <div ref={comparisonRef} className="mb-12 overflow-x-auto">
            <div className="card p-6">
              <h3 className="font-display text-xl font-semibold text-theme-text mb-6">
                Compare all features
              </h3>
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-theme-border-muted">
                    <th className="text-left py-3 pr-4 text-sm font-medium text-theme-text-secondary">
                      Feature
                    </th>
                    {allPlans.map((plan) => (
                      <th key={plan.tier} className="text-center py-3 px-2 text-sm font-medium text-theme-text">
                        {formatPlanName(plan.name)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.key} className="border-b border-theme-border-muted last:border-0">
                      <td className="py-3 pr-4 text-sm text-theme-text-secondary">
                        {row.label}
                      </td>
                      {allPlans.map((plan) => {
                        const features = getAllFeatures(plan.tier, plan.subscriptionCredits);
                        if (row.key === 'hasAds') {
                          return (
                            <td key={plan.tier} className="text-center py-3 px-2">
                              {features.hasAds ? (
                                <X className="h-5 w-5 text-theme-text-muted mx-auto" />
                              ) : (
                                <Check className="h-5 w-5 text-theme-success mx-auto" />
                              )}
                            </td>
                          );
                        }
                        const value = features[row.key as keyof AllFeatures];
                        return (
                          <td key={plan.tier} className="text-center py-3 px-2 text-sm text-theme-text">
                            {value}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* FAQ or additional info */}
        <div className="mt-12 text-center">
          <p className="text-theme-text-secondary">
            Questions? <a href="mailto:support@llambada.com" className="text-theme-primary hover:underline">Contact us</a>
          </p>
          {isPaidUser && (
            <p className="mt-4">
              <button
                onClick={() => setShowCancelModal(true)}
                className="text-sm text-theme-text-muted hover:text-theme-error transition-colors"
              >
                Cancel subscription
              </button>
            </p>
          )}
        </div>

        {/* Cancellation Success Banner */}
        {cancelResult?.success && (
          <div className="fixed bottom-4 right-4 max-w-md p-4 rounded-xl bg-theme-surface border border-theme-border shadow-xl animate-slide-up">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 p-2 rounded-lg bg-emerald-500/20">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-theme-text">Subscription Cancelled</h4>
                <p className="text-sm text-theme-text-secondary mt-1">{cancelResult.message}</p>
              </div>
              <button
                onClick={() => setCancelResult(null)}
                className="p-1 rounded text-theme-text-muted hover:text-theme-text"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Subscription Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
        size="sm"
      >
        <div className="text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-theme-error-bg mb-4">
            <AlertTriangle className="h-7 w-7 text-theme-error" />
          </div>
          <p className="text-theme-text mb-2">
            Are you sure you want to cancel your subscription?
          </p>
          <p className="text-sm text-theme-text-secondary mb-6">
            Your subscription will remain active until {subscriptionExpiresAt ? subscriptionExpiresAt.toLocaleDateString() : 'the end of your billing period'}. After that, you'll be downgraded to the Free plan. Any PAYG credits you've purchased will remain available.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={() => setShowCancelModal(false)}
              className="btn btn-secondary flex-1"
            >
              Keep Subscription
            </button>
            <button
              onClick={handleCancelSubscription}
              disabled={loading}
              className="btn btn-danger flex-1"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Cancel Subscription'}
            </button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
